import React, { useState } from 'react'
import { Menu, MenuItem } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import dayjs from 'dayjs'

import { SingleGoalUpdate } from 'Generated/graphql-types'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  Actions,
  Avatar,
  Card,
  Description,
  Details,
  TopHalf,
  UserName,
  AssociatedTask,
  MenuButton,
  DateInfo,
  MediaContainer,
  Image,
} from './Update.styled'
import UserAvatar from '../../../../SharedComponents/UserAvatar'

type Props = {
  update: SingleGoalUpdate
  onDeleteUpdate: () => void
  onEditUpdate: () => void
}

export const Update = ({ update, onDeleteUpdate, onEditUpdate }: Props) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(menuAnchorEl)

  const { userConfig: { id: userId } = {} } = useAppState()
  const lastUpdatedUser = {
    id: update.lastUpdatedUserId,
    firstName: update.lastUpdatedUserFirstname,
    lastName: update.lastUpdatedUserLastname,
  }
  const associatedTaskTitle = update.taskTitle
  const isEditable = userId === lastUpdatedUser.id
  const formattedDate = update.reportedAt
    ? dayjs(update.reportedAt).format('dddd, MMMM D, YYYY')
    : ''

  // Get user initials for avatar
  const userInitials =
    `${lastUpdatedUser.firstName?.charAt(0) || ''}${
      lastUpdatedUser.lastName?.charAt(0) || ''
    }`.toUpperCase() || 'JR'

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  const handleEdit = () => {
    handleMenuClose()
    onEditUpdate()
  }

  const handleDelete = () => {
    handleMenuClose()
    onDeleteUpdate()
  }

  const showImage = false

  return (
    <Card key={update.id}>
      <TopHalf>
        <Details>
          <Avatar>
            <div
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: '#E5E5E5',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#555',
              }}
            >
              {userInitials}
            </div>
          </Avatar>
          <div>
            <UserName>
              {`${update.lastUpdatedUserFirstname} ${update.lastUpdatedUserLastname}`}
            </UserName>
            <DateInfo>{formattedDate}</DateInfo>
            {associatedTaskTitle && (
              <AssociatedTask>FROM TASK {associatedTaskTitle}</AssociatedTask>
            )}
          </div>
        </Details>
        {isEditable && (
          <Actions>
            <MenuButton
              aria-label='more'
              aria-controls='snapshot-menu'
              aria-haspopup='true'
              onClick={handleMenuOpen}
            >
              <MoreHorizIcon fontSize='small' />
            </MenuButton>
            <Menu
              id='snapshot-menu'
              anchorEl={menuAnchorEl}
              keepMounted
              open={menuOpen}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  width: '180px',
                },
              }}
            >
              <MenuItem onClick={handleEdit} sx={{ gap: '12px', py: '10px' }}>
                <EditIcon fontSize='small' />
                Edit
              </MenuItem>
              <MenuItem onClick={handleDelete} sx={{ gap: '12px', py: '10px' }}>
                <DeleteIcon fontSize='small' />
                Delete
              </MenuItem>
            </Menu>
          </Actions>
        )}
      </TopHalf>
      <Description>{update.description}</Description>

      {showImage && (
        <MediaContainer>
          <Image
            src='https://via.placeholder.com/600x400?text=Snapshot+Image'
            alt='Snapshot image'
          />
        </MediaContainer>
      )}
    </Card>
  )
}
